/**
 * navegacion.ts — el árbol del menú, como dato.
 *
 * Hasta el 2026-09-28 eran **170 líneas de HTML escritas a mano** en
 * `index.html`, con la visibilidad resuelta a base de `style="display:none"` y
 * clases (`nav-staff`, `nav-admin`) que `app.js` encendía y apagaba recorriendo
 * el DOM. Añadir un destino era tocar dos archivos y acordarse de la clase.
 *
 * Aquí es una lista. Quién ve cada cosa lo decide el mismo criterio de siempre
 * —`Auth.hasModule`, `Auth.isStaff`, `Auth.can`—, pero preguntado en un sitio en
 * vez de repartido entre el marcado y tres barridos del documento.
 *
 * ## Lo que NO cambia, y conviene que se note
 *
 * Ningún destino cambia de sitio, de nombre ni de dirección. Es la misma
 * restricción que se impuso la reorganización del menú del 2026-07-30 y la
 * migración de Administración: **renombrar es otro cambio, con su propio
 * motivo**. Lo comprueba `test_armazon_pantalla.py` comparando esta lista con el
 * `index.html` mientras el marcado siga existiendo.
 */

/** Qué hace falta para ver un destino. Ausente = lo ve todo el mundo. */
export interface Requisito {
  /** El módulo que tiene que estar habilitado (`Auth.hasModule`). */
  modulo?: string;
  /** `modulo:accion`, para los que piden un permiso concreto (`Auth.can`). */
  permiso?: string;
  /** Solo para quien es de gestión (`Auth.isStaff`). */
  soloGestion?: boolean;
  /** Del bloque de Administración. Ese bloque entero se enseña a quien tenga
   *  alguno de sus módulos O sea de gestión; cada destino dentro se filtra
   *  además por su propio módulo y permiso. */
  deAdministracion?: boolean;
}

export interface Destino extends Requisito {
  /** El hash SIN `#`, tal como lo escribe el menú. */
  hash: string;
  /** La clave del `PAGE_MAP` — la usa el router para saber cuál está activa. */
  page: string;
  icono: string;
  etiqueta: string;
}

export interface Grupo extends Requisito {
  clave: string;
  icono: string;
  etiqueta: string;
  destinos: Destino[];
}

/** Inicio va suelto y primero: no pertenece a ningún módulo y es donde aterriza
 *  todo el mundo al entrar. */
export const INICIO: Destino = {
  hash: 'dashboard', page: 'dashboard', modulo: 'dashboard',
  icono: 'bi-house-door-fill', etiqueta: 'Inicio',
};

export const GRUPOS: Grupo[] = [
  {
    clave: 'novedades', icono: 'bi-bell', etiqueta: 'Novedades',
    destinos: [
      { hash: 'novedades', page: 'feed', modulo: 'feed', icono: 'bi-newspaper', etiqueta: 'Publicaciones' },
      { hash: 'celebraciones', page: 'celebrations', modulo: 'celebraciones', icono: 'bi-balloon-heart', etiqueta: 'Celebraciones' },
      { hash: 'kudos', page: 'kudos', modulo: 'kudos', icono: 'bi-award', etiqueta: 'Reconocimientos' },
      { hash: 'directorio', page: 'directory', modulo: 'directorio', icono: 'bi-people', etiqueta: 'Directorio' },
      { hash: 'espacios', page: 'communities', modulo: 'comunidades', icono: 'bi-collection', etiqueta: 'Comunidades' },
    ],
  },
  {
    clave: 'autogestion', icono: 'bi-person-gear', etiqueta: 'Autogestión',
    destinos: [
      { hash: 'perfil', page: 'profile', icono: 'bi-person-circle', etiqueta: 'Mi perfil' },
      { hash: 'tramites', page: 'solicitudes', icono: 'bi-file-earmark-text', etiqueta: 'Solicitudes' },
    ],
  },
  {
    clave: 'formacion', icono: 'bi-mortarboard', etiqueta: 'Formación',
    destinos: [
      { hash: 'cursos', page: 'courses', modulo: 'cursos', icono: 'bi-play-circle', etiqueta: 'Cursos' },
      { hash: 'rutas', page: 'learning-paths', modulo: 'cursos', icono: 'bi-signpost-split', etiqueta: 'Rutas de Aprendizaje' },
      { hash: 'competencias', page: 'competencies', modulo: 'cursos', icono: 'bi-diagram-3', etiqueta: 'Competencias' },
      { hash: 'biblioteca', page: 'resources', modulo: 'recursos', icono: 'bi-journal-bookmark', etiqueta: 'Biblioteca' },
    ],
  },
  {
    clave: 'eventos', icono: 'bi-calendar-event', etiqueta: 'Eventos',
    destinos: [
      { hash: 'eventos', page: 'events', modulo: 'eventos', icono: 'bi-calendar-event', etiqueta: 'Eventos' },
      { hash: 'encuestas', page: 'surveys', modulo: 'encuestas', icono: 'bi-clipboard-check', etiqueta: 'Encuestas' },
      { hash: 'eventos-calendario', page: 'calendar', modulo: 'eventos', icono: 'bi-calendar3', etiqueta: 'Calendario' },
    ],
  },
  // Los dos grupos de gestión van al final a propósito: un colaborador raso ve
  // una lista más corta y sin huecos, en vez de dos títulos vacíos en medio.
  {
    clave: 'gestion-conocimiento', icono: 'bi-database', etiqueta: 'Recursos y Datos',
    soloGestion: true,
    destinos: [
      { hash: 'gestion-conocimiento', page: 'private-records', modulo: 'recursos_privados', icono: 'bi-hdd-network', etiqueta: 'Bases de Datos' },
      { hash: 'gestion-conocimiento/recoleccion', page: 'private-records', modulo: 'recursos_privados', icono: 'bi-input-cursor-text', etiqueta: 'Recolección de Datos' },
    ],
  },
  {
    clave: 'reportes', icono: 'bi-bar-chart-line', etiqueta: 'Reportes',
    soloGestion: true,
    destinos: [
      { hash: 'analitica', page: 'analytics', modulo: 'analitica', icono: 'bi-graph-up-arrow', etiqueta: 'Analítica' },
      { hash: 'informes', page: 'reports', modulo: 'reportes', icono: 'bi-file-earmark-bar-graph', etiqueta: 'Informes' },
      { hash: 'induccion', page: 'induction', soloGestion: true, icono: 'bi-clipboard2-check', etiqueta: 'Inducción' },
    ],
  },
];

/** Administración no es un grupo plegable: son tres destinos sueltos bajo su
 *  propio título, como estaban. */
export const ADMINISTRACION: Destino[] = [
  { hash: 'admin', page: 'admin', modulo: 'administracion', deAdministracion: true, icono: 'bi-gear', etiqueta: 'Panel de administración' },
  { hash: 'centro-notificaciones', page: 'communications', modulo: 'notificaciones', permiso: 'notificaciones:manage', deAdministracion: true, icono: 'bi-megaphone', etiqueta: 'Centro de Notificaciones' },
  { hash: 'auditoria', page: 'audit', modulo: 'administracion', deAdministracion: true, icono: 'bi-shield-lock', etiqueta: 'Auditoría' },
];

/** ¿Se enseña el bloque de Administración? El mismo criterio del armazón:
 *  tener alguno de sus dos módulos, o ser de gestión. */
export function hayAdministracion(): boolean {
  const modulos = Auth.getModules();
  return ['notificaciones', 'administracion'].some((m) => modulos.includes(m)) || Auth.isStaff();
}

/** Los destinos de Administración que ve quien mira. */
export function administracionVisible(): Destino[] {
  return hayAdministracion() ? ADMINISTRACION.filter(puedeVer) : [];
}

/**
 * ¿Quien mira puede ver esto?
 *
 * El mismo criterio que aplicaba el vanilla, en un solo sitio. Ante la duda
 * responde que NO: esto solo decide qué se enseña, y quien autoriza de verdad es
 * el servidor — enseñar de menos es un fastidio, enseñar de más es prometer una
 * pantalla que va a responder 403.
 */
export function puedeVer(r: Requisito): boolean {
  if (r.soloGestion && !Auth.isStaff()) return false;
  // `dashboard` no es un módulo que se conceda: Inicio lo ve todo el mundo. El
  // armazón lo tenía como excepción escrita (`|| mod === 'dashboard'`).
  if (r.modulo && r.modulo !== 'dashboard' && !Auth.hasModule(r.modulo)) return false;
  if (r.permiso) {
    const [modulo, accion] = r.permiso.split(':');
    // El paso libre para coordinador y admin estaba en `_cumplePermiso` del
    // armazón, y se conserva: sin él, un coordinador perdería el Centro de
    // Notificaciones del menú aunque el servidor se lo permita.
    const rol = Auth.getUser()?.role;
    if (!Auth.can(modulo!, accion) && rol !== 'coordinador' && rol !== 'admin') return false;
  }
  return true;
}

/** Un grupo sin ningún destino visible no se enseña: un título que despliega
 *  una lista vacía es peor que no estar. */
export function gruposVisibles(): Array<Grupo & { destinos: Destino[] }> {
  return GRUPOS
    .filter(puedeVer)
    .map((g) => ({ ...g, destinos: g.destinos.filter(puedeVer) }))
    .filter((g) => g.destinos.length > 0);
}

/** Todos los destinos, planos, en el orden del menú. Lo usan «Recientes» y la
 *  estrella de favoritos para saber CÓMO se llama y qué icono tiene cada
 *  destino, sin preguntárselo al DOM. */
export const TODOS: Array<Destino & { grupo: string }> = [
  { ...INICIO, grupo: '' },
  ...GRUPOS.flatMap((g) => g.destinos.map((d) => ({ ...d, grupo: g.clave }))),
  ...ADMINISTRACION.map((d) => ({ ...d, grupo: 'administracion' })),
];

/**
 * El destino que corresponde a una navegación.
 *
 * Reproduce la búsqueda que hacía `trackRecent()` en el DOM del menú, y por eso
 * el orden importa: **primero el hash exacto**, después la página.
 *
 * Con sub-ruta hay dos casos y se distinguen así:
 *
 * - `gestion-conocimiento/recoleccion` ES un destino del menú. Coincide por hash
 *   exacto y se guarda tal cual, porque «Recolección de Datos» es su nombre.
 * - `perfil/ficha` NO lo es —lo abre el botón «Completar» del aviso de datos
 *   pendientes—. No coincide por hash, cae al `page`, y se devuelve el destino
 *   base: así «Recientes» guarda «Mi perfil» apuntando a `#perfil` y no una fila
 *   que dice «Mi perfil» y abre un panel.
 */
export function destinoPara(page: string, hash: string): (Destino & { grupo: string }) | null {
  return TODOS.find((d) => d.hash === hash) ?? TODOS.find((d) => d.page === page) ?? null;
}
